import type { VehicleResult, RankEntry } from '../../types';
import { VEHICLE_COLORS } from './TCOSummaryTable';

interface Props {
  vehicles: VehicleResult[];
  financialRanking: RankEntry[];
}

function fmt$(v: number): string {
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getNum(v: VehicleResult, section: string, key: string): number {
  const sec = v[section as keyof VehicleResult] as Record<string, unknown> | null;
  if (!sec) return 0;
  const val = sec[key];
  return typeof val === 'number' ? val : 0;
}

interface BreakEven {
  vehicleId: string;
  label: string;
  colorIdx: number;
  isWinner: boolean;
  tco: number;
  winnerTco: number;
  tcoDiff: number;
  // Break-even resale: sell for more → reduce TCO
  breakEvenResale: number;
  currentResidual: number;
  // Break-even purchase: buy cheaper → reduce TCO
  breakEvenPurchase: number;
  currentPurchase: number;
}

export default function BreakEvenPanel({ vehicles, financialRanking }: Props) {
  if (vehicles.length < 2) return null;

  const winnerEntry = financialRanking.find((e) => e.rank === 1);
  const winnerVehicle = winnerEntry ? vehicles.find((v) => v.vehicle_id === winnerEntry.vehicle_id) : null;
  if (!winnerVehicle) return null;

  const winnerTco = getNum(winnerVehicle, 'tco', 'total_tco');

  const cards: BreakEven[] = vehicles.map((v, i) => {
    const tco = getNum(v, 'tco', 'total_tco');
    const tcoDiff = tco - winnerTco;
    const isWinner = v.vehicle_id === winnerVehicle.vehicle_id;
    // v1.42.4 Fix 1: read from tco.residual_value_reclaimed (Argonne) so the
    // residual shown here matches the "Residual Value Reclaimed" row in
    // TCOSummaryTable. The engine's depreciation.residual_value derives from a
    // different curve (user_residual_value_year_5) and diverges ~3%.
    const currentResidual = getNum(v, 'tco', 'residual_value_reclaimed');
    const currentPurchase = (() => {
      // Reconstruct purchase price from the engine's depreciation totals — these
      // are still curve-consistent (residual_value_engine + total_depreciation =
      // purchase_price by construction in compute_depreciation).
      const residualEngine = getNum(v, 'depreciation', 'residual_value');
      const totalDep = getNum(v, 'depreciation', 'total_depreciation');
      return residualEngine + totalDep;
    })();

    // break-even resale = current residual + tcoDiff (need to get that much more back on sale)
    const breakEvenResale = currentResidual + tcoDiff;
    // break-even purchase = current purchase - tcoDiff (lower price reduces depreciation)
    const breakEvenPurchase = currentPurchase - tcoDiff;

    return {
      vehicleId: v.vehicle_id,
      label: v.label,
      colorIdx: i,
      isWinner,
      tco,
      winnerTco,
      tcoDiff,
      breakEvenResale,
      currentResidual,
      breakEvenPurchase,
      currentPurchase,
    };
  });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div
          key={card.vehicleId}
          className="rounded-xl p-4 flex flex-col gap-3"
          style={{ background: 'var(--panel)', border: '1px solid var(--border)' }}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <p
              className="font-semibold text-sm leading-snug"
              style={{ color: VEHICLE_COLORS[card.colorIdx] }}
            >
              {card.label}
            </p>
            {card.isWinner && (
              <span className="shrink-0 inline-flex items-center gap-1 bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs font-semibold px-2 py-0.5 rounded-full">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Winner
              </span>
            )}
          </div>

          {card.isWinner ? (
            <div className="text-xs leading-relaxed" style={{ color: 'var(--white-dim)' }}>
              Lowest TCO of{' '}
              <span className="text-emerald-400 font-semibold">{fmt$(card.tco)}</span>.
              <br />
              This vehicle is already the financial best choice.
            </div>
          ) : (
            <>
              <div className="text-xs mb-1" style={{ color: 'var(--white-dim)' }}>
                TCO is{' '}
                <span className="text-red-400 font-semibold">{fmt$(card.tcoDiff)}</span> more than
                the winner.
              </div>

              {/* Break-even resale */}
              <div className="rounded-lg px-3 py-2.5" style={{ background: 'var(--panel2)', border: '1px solid var(--border)' }}>
                <p className="text-xs mb-1 font-medium uppercase" style={{ color: 'var(--white-dim)', fontFamily: "'Share Tech Mono', monospace", letterSpacing: '0.2em' }}>
                  Break-even Resale Price
                </p>
                <p className="text-base font-bold" style={{ color: 'var(--white)' }}>{fmt$(card.breakEvenResale)}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--white-dim)' }}>
                  Currently estimated at {fmt$(card.currentResidual)}
                </p>
              </div>

              {/* Break-even purchase */}
              <div className="rounded-lg px-3 py-2.5" style={{ background: 'var(--panel2)', border: '1px solid var(--border)' }}>
                <p className="text-xs mb-1 font-medium uppercase" style={{ color: 'var(--white-dim)', fontFamily: "'Share Tech Mono', monospace", letterSpacing: '0.2em' }}>
                  Break-even Purchase Price
                </p>
                <p className="text-base font-bold" style={{ color: 'var(--white)' }}>{fmt$(card.breakEvenPurchase)}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--white-dim)' }}>
                  Currently {fmt$(card.currentPurchase)}
                </p>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
